import { Ban } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, MaskLines } from "@/components/Motion";
import { CallButton, ConsultButton } from "@/components/Buttons";
import { PlanOptions, QuoteProcess } from "@/components/PlanOptions";
import { VisitIncludes } from "@/components/VisitIncludes";
import { CTABand } from "@/components/CTABand";
import { SectionHead, usePageMeta } from "@/components/Common";
import { ADDONS, PLANS_INTRO, SETUP_FEE, WEEKLY_TERMS, RECURRING_TERMS, QUOTE_CTA } from "@/data/site";

const TERMS = [
  "All services are provided under a clear written quote and service agreement. Proposals are based on total property size, layout, detached structures, access requirements, travel distance and property-specific needs. Additional services are quoted separately.",
  "Visit frequency options include one-time visits, exactly two scheduled visits per calendar month, Weekly Home Watch with 52 scheduled visits annually billed in 12 equal monthly installments, and customized schedules.",
  SETUP_FEE,
  RECURRING_TERMS,
  WEEKLY_TERMS,
];

const NOT_INCLUDED = "Standard home watch visits do not include unlimited vendor waiting, airport transportation, plant watering or equipment maintenance.";

export default function Pricing() {
  usePageMeta("Home Watch Plans | Wellington & Royal Palm Beach FL | First Due Home Watch & Concierge", "Residential home watch plans in Wellington, Royal Palm Beach and western Palm Beach County: one-time, twice-monthly, weekly and customized visits. Request a personalized quote.");
  return (
    <main data-testid="pricing-page">
      <PageHero testId="pricing-hero" eyebrow="Home Watch Plans" lines={["Home Watch Plans", "Tailored to Your Property."]} intro={PLANS_INTRO} img="/images/hero.jpg" alt="South Florida estate home">
        <ConsultButton testId="pricing-hero-quote-btn" label={QUOTE_CTA} dark />
        <CallButton testId="pricing-hero-call-btn" dark />
      </PageHero>
      <section data-testid="plans-section" className="rule-dots bg-[#F8F7F3] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Service options" title={<MaskLines onView lines={["Choose the Visit Schedule", "That Fits Your Home"]} />} />
          <div className="mt-14"><PlanOptions /></div>
          <div className="mt-14"><QuoteProcess testId="plans-quote" /></div>
        </div>
      </section>
      <VisitIncludes />

      <section data-testid="addons-section" className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="Quoted separately" title={<MaskLines onView lines={["Additional", "Concierge Services"]} />} />
          </div>
          <Reveal className="lg:col-span-8">
            <ul data-testid="addons-table" className="border-t-2 border-[#14263D]">
              {ADDONS.map(([s, note]) => (
                <li key={s} className="group border-b border-[#14263D]/10 py-4 transition-colors hover:bg-[#F8F7F3]">
                  <span className="inline-block text-base text-[#14263D] transition-transform duration-300 group-hover:translate-x-2 md:text-lg">{s}</span>
                  {note && <span className="mt-1 block max-w-xl text-sm leading-relaxed text-[#5F6B76]">{note}</span>}
                </li>
              ))}
            </ul>
            <p data-testid="not-included-note" className="mt-8 flex items-start gap-3 rounded-[4px] bg-[#F8F7F3] p-5 text-sm leading-relaxed text-[#14263D]">
              <Ban className="mt-0.5 h-4 w-4 shrink-0 text-[#A52A32]" />{NOT_INCLUDED}
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="pricing-terms" className="bg-[#14263D] py-24 text-white md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead light eyebrow="The fine print, in plain English" title="Plan Terms" />
          </div>
          <ol className="lg:col-span-8">
            {TERMS.map((t, i) => (
              <Reveal as="li" key={t.title || t} delay={i * 0.05} className="flex gap-6 border-b border-white/10 py-5 text-base leading-relaxed text-white/90">
                <span className="font-mono text-xs text-[#f0b3b7]">{String(i + 1).padStart(2, "0")}</span>
                {t.title ? (
                  <div data-testid={`term-${t.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <p className="font-display text-2xl font-semibold text-white">{t.title}</p>
                    {t.text.map((p) => <p key={p} className="mt-2">{p}</p>)}
                  </div>
                ) : t}
              </Reveal>
            ))}
            <div className="mt-10 flex flex-wrap gap-4">
              <ConsultButton testId="pricing-consult-btn" label={QUOTE_CTA} dark />
              <CallButton testId="pricing-call-btn" dark />
            </div>
          </ol>
        </div>
      </section>
      <CTABand testId="pricing-cta" />
    </main>
  );
}
